/**
 * Recent sessions with roll-ups — turn count, interactions, provisionals,
 * adaptations. Shows the shape of each session at a glance so we can
 * see "that 5-minute session produced 37 turns, 2 adaptations, 0
 * unresolved" without digging into SQLite by hand.
 *
 * Each row also carries a **Reprocess** action so we can re-run the
 * full processing pipeline on an already-recorded session file.
 * Invaluable for testing pipeline changes — we can wipe voiceprints,
 * re-enroll, tweak thresholds, and re-score the same audio over and
 * over to compare apples-to-apples instead of having to capture new
 * audio with the same speakers present.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Repeat } from "lucide-react";

import { devListSessions } from "@/hooks/useDevDiagnostics";
import { processSession } from "@/hooks/useSessionProcessor";
import type { DevSessionSummary } from "@/types/devMode";

import {
  formatAbsoluteTime,
  formatDuration,
  formatRelativeTime,
  truncateId,
} from "./devFormatters";

export function SessionsTab() {
  const [sessions, setSessions] = useState<DevSessionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reprocessing, setReprocessing] = useState<string | null>(null);
  const [lastReprocess, setLastReprocess] = useState<{ id: string; ok: boolean; message: string } | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const rows = await devListSessions(30);
      setSessions(rows);
    } catch (err) {
      setError(String(err));
    }
  }, []);

  const handleReprocess = useCallback(
    async (session: DevSessionSummary) => {
      if (!session.audioPath) {
        setLastReprocess({ id: session.id, ok: false, message: "no audio path on this session" });
        return;
      }
      setReprocessing(session.id);
      setLastReprocess(null);
      try {
        const result = await processSession({
          sessionId: session.id,
          audioPath: session.audioPath,
          startedAt: session.startedAt,
          endedAt: session.endedAt,
          durationSeconds: session.durationSeconds,
        });
        setLastReprocess({
          id: session.id,
          ok: true,
          message: `${result.turns.length} turns · ${result.interactions.length} matched · ${result.provisionalSpeakers.length} open · ${result.adaptationCount} adapts`,
        });
        void refresh();
      } catch (err) {
        setLastReprocess({ id: session.id, ok: false, message: String(err) });
      } finally {
        setReprocessing(null);
      }
    },
    [refresh]
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) {
    return <p className="p-4 text-sm text-red-400">Failed to load: {error}</p>;
  }

  if (!sessions) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-white/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading sessions…
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-white/40">
        No sessions recorded yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/40">
          {sessions.length} session{sessions.length === 1 ? "" : "s"}
        </p>
        <button
          onClick={refresh}
          className="flex items-center gap-1 text-[11px] text-white/50 active:opacity-60"
        >
          <RefreshCw className="h-3 w-3" />
          refresh
        </button>
      </div>

      {sessions.map((session) => {
        const isReprocessing = reprocessing === session.id;
        const lastResult = lastReprocess?.id === session.id ? lastReprocess : null;
        const hasAudio = !!session.audioPath;
        return (
          <div
            key={session.id}
            className="rounded-lg border border-white/10 bg-white/[0.02] p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-[14px] font-medium text-white">
                {truncateId(session.id)}
              </p>
              <span className="text-[11px] text-white/40">
                {formatRelativeTime(session.startedAt)}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-white/50">
              {formatAbsoluteTime(session.startedAt)} ·{" "}
              {formatDuration(session.durationSeconds)} · state{" "}
              <span className="font-mono text-white/70">{session.state}</span>
            </p>
            <div className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
              <Stat label="turns" value={session.turnCount} />
              <Stat label="matched" value={session.interactionCount} />
              <Stat label="open" value={session.provisionalCount} />
              <Stat
                label="adapts"
                value={session.adaptationCount}
                highlight={session.adaptationCount > 0}
              />
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => handleReprocess(session)}
                disabled={!hasAudio || isReprocessing || reprocessing !== null}
                className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/40 bg-yellow-400/5 px-2.5 py-1 text-[11px] font-medium text-yellow-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isReprocessing ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Reprocessing…
                  </>
                ) : (
                  <>
                    <Repeat className="h-3 w-3" />
                    Reprocess
                  </>
                )}
              </button>
              {!hasAudio && (
                <span className="text-[10px] text-white/30">audio missing</span>
              )}
              {lastResult && (
                <span
                  className={`truncate text-[10px] ${
                    lastResult.ok ? "text-emerald-300/80" : "text-red-400/80"
                  }`}
                >
                  {lastResult.message}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface StatProps {
  label: string;
  value: number;
  highlight?: boolean;
}

function Stat({ label, value, highlight }: StatProps) {
  return (
    <div className="flex flex-col items-center rounded-md border border-white/10 bg-black/20 p-1.5">
      <span
        className={`font-mono text-[13px] font-semibold ${
          highlight ? "text-emerald-300" : "text-white/80"
        }`}
      >
        {value}
      </span>
      <span className="text-[9px] uppercase tracking-[0.15em] text-white/40">
        {label}
      </span>
    </div>
  );
}
